'use client';

import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  CalendarClock,
  Clock,
  Hash,
  Link2,
  Lock,
  Mail,
  Phone,
  Search,
  Type,
  Upload,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/** Même enveloppe que la recherche institution / {@link InstitutionStyledSelect} (carte + anneau primary). */
export const fieldShellWrapperClass = cn(
  'flex w-full min-h-[2.75rem] items-center gap-3 rounded-lg border px-2.5 py-2 text-sm transition-all',
  'border-gray-200/90 bg-gray-50/80 dark:border-gray-600 dark:bg-gray-900/50',
  'bg-white text-primary-900 shadow-sm ring-2 ring-primary-500/35 dark:bg-gray-800 dark:text-primary-50 dark:ring-primary-400/40',
);

export const fieldShellWrapperErrorClass = cn(
  'border-red-200 ring-2 ring-red-500/35 dark:border-red-800/60 dark:ring-red-400/35',
);

const leadSlotBoxClass = (dense?: boolean, className?: string) =>
  cn(
    'flex shrink-0 items-center justify-center rounded-lg border',
    dense ? 'h-7 w-7' : 'h-9 w-9',
    'border-primary-200 bg-primary-50 text-primary-600 dark:border-primary-800/60 dark:bg-primary-950/50 dark:text-primary-300',
    className,
  );

/** Pastille + icône Lucide (recherche, type implicite, etc.). */
export function FieldShellLeadIcon({
  Icon,
  dense,
  className,
}: {
  Icon: LucideIcon;
  dense?: boolean;
  className?: string;
}) {
  const I = Icon;
  return (
    <span className={leadSlotBoxClass(dense, className)}>
      <I className={cn('shrink-0 opacity-90', dense ? 'h-3.5 w-3.5' : 'h-4 w-4')} aria-hidden />
    </span>
  );
}

/** Même pastille pour un nœud React déjà fourni (ex. icône custom). */
export function FieldShellLeadSlot({
  children,
  dense,
  className,
}: {
  children: ReactNode;
  dense?: boolean;
  className?: string;
}) {
  return (
    <span className={leadSlotBoxClass(dense, className)}>
      <span className={cn('flex items-center justify-center text-primary-600 dark:text-primary-300 [&>svg]:shrink-0', dense ? '[&>svg]:h-3.5 [&>svg]:w-3.5' : '[&>svg]:h-4 [&>svg]:w-4')}>
        {children}
      </span>
    </span>
  );
}

export function fieldShellInnerInputClass(dense?: boolean, className?: string) {
  return cn(
    'min-w-0 flex-1 border-0 bg-transparent p-0 font-medium leading-snug text-primary-900 shadow-none ring-0 outline-none',
    dense ? 'text-xs' : 'text-sm',
    'placeholder:font-normal placeholder:text-gray-500 dark:text-primary-50 dark:placeholder:text-gray-400',
    'focus:ring-0 focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-60',
    'file:me-3 file:rounded-md file:border-0 file:bg-primary-100 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-800 hover:file:bg-primary-200 dark:file:bg-primary-950/50 dark:file:text-primary-200',
    className,
  );
}

/** Alias historique (recherche institution) — identique à {@link fieldShellWrapperClass}. */
export const institutionListSearchShellClass = fieldShellWrapperClass;

/** Icône de tête par défaut selon le type HTML (champs texte, auth, filtres…). */

export function defaultLucideForInputType(type: string | undefined): LucideIcon {
  switch (type) {
    case 'email':
      return Mail;
    case 'password':
      return Lock;
    case 'search':
      return Search;
    case 'tel':
      return Phone;
    case 'url':
      return Link2;
    case 'number':
      return Hash;
    case 'date':
      return Calendar;
    case 'datetime-local':
      return CalendarClock;
    case 'time':
      return Clock;
    case 'file':
      return Upload;
    default:
      return Type;
  }
}
